import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AddShoppingCartEntryRequestDto, AudioTrack } from '@faro/order-angular-client';
import { resetAllFilters, resetSearch, triggerResetFacetSearch } from 'src/app/search/search-state/search-options.actions';
import { dispatchEmptySearch } from 'src/app/search/search-state/search-result.actions';
import { Router } from '@angular/router';

@Injectable()
export class HostConnectorService {
    private readonly _webView: any = undefined;

    constructor(private readonly store: Store,
        private readonly router: Router
    ) {
        // are we running in chromium webview container host?
        const chrome = (window as any).chrome;
        if (chrome) {
            this._webView = chrome.webview;
        }
        if (this._webView) {
            this._webView.addEventListener('message', (e: MessageEvent) => this.onMessage(e));
        }
    }

    public isAppInWebViewContainer(): boolean {
        return !!this._webView;
    }

    registerMessageReceiver(callback: (event: MessageEvent) => void) {
        if (this._webView) {
            this._webView.addEventListener('message', callback);
        }
    }

    private onMessage(event: MessageEvent): void {
        if (event.data === "goHome") {
            this.goHome();
        }
    }

    async goHome() {
        this.store.dispatch(triggerResetFacetSearch());
        this.store.dispatch(resetSearch());
        this.store.dispatch(resetAllFilters());
        this.store.dispatch(dispatchEmptySearch());
        return this.router.navigate(['/']);
    }

    private emitMessage(message: HostMessage): void {
        if (this._webView) {
            this._webView.postMessage(message);
        }
    }

    private getBasicEntityMessage(entityInfo: EntityInfo, action: Action): EntityMessage | undefined {
        let id = entityInfo.sequenceId ?? entityInfo.itemId ?? entityInfo.programId;
        let entityType = entityInfo.sequenceId ? EntityType.Shot : (entityInfo.itemId ? EntityType.Item : EntityType.Program);
        if (id) {
            return {
                messageType: 'EntityMessage',
                action,
                type: entityType,
                id,
            };
        }
        return undefined;
    }

    emitDocSetMessage(entityInfo: EntityInfo) {
        const entityMessage = this.getBasicEntityMessage(entityInfo, Action.DocSet);
        if (entityMessage) {
            this.emitMessage(entityMessage);
        }
    }

    emitDetailMessage(entityInfo: EntityInfo) {
       const entityMessage = this.getBasicEntityMessage(entityInfo, Action.Detail);
        if (entityMessage) {
            this.emitMessage(entityMessage);
        }
    }

    emitPlayerMessage(entityInfo: EntityInfo) {
       const entityMessage = this.getBasicEntityMessage(entityInfo, Action.Player);
        if (entityMessage) {
            this.emitMessage(entityMessage);
        }
    }

    emitCatalogingEditorMessage(entityInfo: EntityInfo, ccId?: string) {
        const entityMessage = this.getBasicEntityMessage(entityInfo, Action.Cataloging);
        if (entityMessage) {
            entityMessage.ccId = ccId;
            this.emitMessage(entityMessage);
        }
    }

    emitAddShoppingCartEntryMessage(entry: AddShoppingCartEntryRequestDto): void {
        let id = entry.sequenceId ?? entry.itemId ?? entry.programId;
        let entityType = entry.sequenceId ? EntityType.Shot : (entry.itemId ? EntityType.Item : EntityType.Program);
        if (id) {
            const message: EntityMessage = {
                messageType: 'EntityMessage',
                action: Action.ShoppingCart,
                type: entityType,
                ccId: entry.ccId ?? undefined,
                id,
                remark: entry.remark ?? undefined,
                audioTrack: entry.selectedAudioTrack,
                mediaCutId: entry.mediaCutId ?? undefined,
                vtcIn: entry.vtcIn ?? undefined,
                vtcOut: entry.vtcOut ?? undefined
            };
            this.emitMessage(message);
        }
    }

    emitAction(action: Action): void {
        const message: HostMessage = {
            messageType: 'HostMessage',
            action: action,
        };
        this.emitMessage(message);
    }

    /// Send message to "host" application
    emitStandalonePlayerMessage(message: StandalonePlayerMessage): void {
        // console.log('emitStandalonePlayerMessage', message);
        
        if (this.isAppInWebViewContainer()) {
            this._webView.postMessage(message);
        }

        window.dispatchEvent(new CustomEvent(message.action, { detail: message.playerTime}));
    }
}

interface HostMessage {
    messageType: string;
    action: Action;
}

interface EntityMessage extends HostMessage {
    type: EntityType;
    id: string;
    remark?: string;
    audioTrack?: AudioTrack;
    mediaCutId?: string;
    ccId?: string;
    vtcIn?: string;
    vtcOut?: string;
}

export enum Action {
    Detail = 'Detail',
    DocSet = 'DocSet',
    ShoppingCart = 'ShoppingCart',
    AuthToken = 'AuthToken',
    ActionLog = 'ActionLog',
    Player = "Player",
    Cataloging = "Cataloging",
}

export interface EntityInfo {
    programId: string,
    itemId?: string,
    sequenceId?: string,
}

export enum EntityType {
    Shot = 'Shot',
    Item = 'Item',
    Program = 'Program',
}

export class StandalonePlayerMessage {
    origin = 'StandalonePlayer';
    action: 'None' | 'Ready' | 'PlayerTime' | 'PlayedToEnd' | 'StillImage' | 'SeekStarted' | 'SeekCompleted' | 'Playing' | 'Paused' | 'VolumeInfo' = 'None';
    playerTime?: number;
    base64ImageString?: string;
    muted?: boolean;
    volume?: number;
}
